// Header icon for IoT alerts: unread badge (refreshed every 30 s) and a dropdown
// with the latest unread alerts. Shown only with IoT Live Monitoring access.
// Separate from the ticket notifications bell.
import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Empty, Popover, Tag, Tooltip } from "antd";
import { AlertOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getIotNotificationCount, markIotNotificationsRead } from "../services";
import { IOT_LIVE_MODULE, SEVERITY_COLORS, getModuleAccess, timeAgo } from "./iotCommon";

const POLL_MS = 30000;

function IoTAlertsBell() {
  const navigate = useNavigate();
  const canRead = getModuleAccess(IOT_LIVE_MODULE).read;
  const [count, setCount] = useState(0);
  const [latest, setLatest] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    getIotNotificationCount({ latest: 6 }).then((res) => {
      if (res.status === "success") {
        setCount(res.unread_count || 0);
        setLatest(res.data || []);
      }
    });
  }, []);

  useEffect(() => {
    if (!canRead) return undefined;
    load();
    const timer = setInterval(() => !document.hidden && load(), POLL_MS);
    return () => clearInterval(timer);
  }, [canRead, load]);

  if (!canRead) return null;

  const openItem = (n) => {
    markIotNotificationsRead({ ids: [n.id] }).then(load);
    setOpen(false);
    navigate(n.machine_id ? `/asset/${n.machine_id}` : "/iot/notifications");
  };

  const markAll = () => markIotNotificationsRead({ all: true }).then(load);

  const content = (
    <div style={{ width: 380 }}>
      <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100">
        <span className="font-semibold text-gray-900">IoT Alerts</span>
        {count > 0 && (
          <Button type="link" size="small" onClick={markAll}>
            Mark all as read
          </Button>
        )}
      </div>
      {latest.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No unread alerts" />
      ) : (
        <div className="flex flex-col gap-1 max-h-96 overflow-y-auto">
          {latest.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => openItem(n)}
              className="text-left p-2 rounded-md hover:bg-gray-50 border border-transparent hover:border-gray-200"
            >
              <div className="flex items-start gap-2">
                <Tag color={SEVERITY_COLORS[n.severity]} className="mt-0.5">
                  {n.severity}
                </Tag>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate" title={n.title}>
                    {n.title}
                  </div>
                  <div className="text-xs text-gray-500">
                    {n.category_label} · {timeAgo(n.created_at)}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      <div className="pt-2 mt-2 border-t border-gray-100 text-center">
        <Button
          type="link"
          size="small"
          onClick={() => {
            setOpen(false);
            navigate("/iot/notifications");
          }}
        >
          View all IoT notifications
        </Button>
      </div>
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="bottomRight" open={open} onOpenChange={setOpen}>
      <Tooltip title="IoT alerts">
        <Badge count={count} size="small" overflowCount={99}>
          <Button type="text" icon={<AlertOutlined />} className="hover:bg-gray-100" aria-label="IoT alerts" />
        </Badge>
      </Tooltip>
    </Popover>
  );
}

export default IoTAlertsBell;
